/* eslint-disable @typescript-eslint/no-unsafe-argument */
import {useMemo, useRef, type RefObject} from "react";
import type {ListModuleConfig} from "~/domainModels/listView/listModuleConfig";
import type {OttopilotDataViewerController} from "~/components/ottopilotDataViewer/ottopilotDataViewerController";
import {useOttopilotListCardFooterBindings} from "~/components/ottopilotDataViewer/ottopilotListCardPresentation";
import {
    navigateToScheduleCallRoute,
    type ScheduleCallNavigateFn,
} from "~/components/scheduleCall/navigateToScheduleCallRoute";
import {useScheduleCallEngagement} from "~/components/scheduleCall/useScheduleCallEngagement";
import {useWorkspace, useWorkspaceEventContainerTemplates} from "~/contexts/workspaceContext";
import {resolveWorkspaceLogCallRoute} from "~/domainModels/scheduleCall/resolveWorkspaceLogCallRoute";
import {RowEngagementPanelType, RowEngagementType} from "~/components/ottopilotDataViewer/rowEngagementPanel";
import {dismissDataViewerAsidePanels} from "~/components/ottopilotDataViewer/rowEngagement/dismissDataViewerAsidePanels";
import {resolveEventContainerRowEngagementCapabilities} from "~/components/ottopilotDataViewer/rowEngagement/resolveEventContainerRowEngagementCapabilities";
import type {RowEngagementHandlers} from "~/components/ottopilotDataViewer/rowEngagement/rowEngagementHandlers";
import {useEventContainerArchiveEngagement} from "~/components/ottopilotDataViewer/rowEngagement/useEventContainerArchiveEngagement";
import type {ListNotesPanelOpenContext} from "~/components/notes/listNotesPanelTypes";
import type {TableRowActionsConfig} from "~/components/table/tableRowActions.types";
import type {
    EventContainerConstraint,
    EventContainerScheduleCallDisableConfig,
} from "~/domainModels/workspaceModules/eventContainersModule";
import {
    isEventContainerEngagementRestrictedByGlobalConstraints,
    isEventContainerRowActionDisabledByGlobalConstraints,
} from "~/domainModels/workspaceModules/eventContainerEditPanelConstraints";
import {getScheduleCallDisabledReason} from "~/domainModels/workspaceModules/eventContainerScheduleCallDisable";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {EventContainerWithEvents} from "~/utilities/apis/api";
import {buildEventContainerProfileFromForList} from "~/utilities/eventContainerProfileNavigation";
import {
    getEventContainerDispositionId,
    requireEventContainerTemplate,
} from "~/utilities/ottopilotSemanticColumns";

export function useEventContainerRowEngagement({
    workspaceId,
    navigate,
    listModuleConfig,
    showDisposition,
    showNotes,
    includeCreateDrawer,
    listCreateDrawerItemsCount,
    scheduleRoute,
    onScheduleSuccess,
    queryKeyPrefix,
    controllerRef,
    selectedDispositionEventContainerId,
    notesPanelEventContainerId,
    openNotesPanel,
    closeNotesPanel,
    notesEventContainerSourceLabel,
    onOpenDisposition,
    onCloseDisposition,
    isArchivedView,
    isInQueueView,
    canEditInQueueJourney,
    globalConstraints,
    scheduleCallDisableConfig,
    isAmaron,
}: {
    workspaceId: Uuid;
    navigate: ScheduleCallNavigateFn;
    isArchivedView: boolean;
    isInQueueView: boolean;
    /** When In Queue: edit is enabled only for unassigned journeys. */
    canEditInQueueJourney?: (journey: EventContainerWithEvents) => boolean;
    /** Optional workspace config; when constraints fail, only view/archive/profile stay enabled. */
    globalConstraints?: Array<EventContainerConstraint>;
    scheduleCallDisableConfig?: EventContainerScheduleCallDisableConfig;
    listModuleConfig: ListModuleConfig | null;
    showDisposition: boolean;
    showNotes: boolean;
    includeCreateDrawer: boolean;
    listCreateDrawerItemsCount: number;
    /** Optional override; workspace default used when empty. */
    scheduleRoute?: string;
    onScheduleSuccess?: () => void;
    queryKeyPrefix: Array<unknown>;
    controllerRef: RefObject<OttopilotDataViewerController<EventContainerWithEvents> | null>;
    selectedDispositionEventContainerId: Uuid | null;
    /** Event Container id when the notes panel is open; drives the table notes active state. */
    notesPanelEventContainerId: Uuid | null;
    openNotesPanel: (context: ListNotesPanelOpenContext) => void;
    closeNotesPanel: () => void;
    notesEventContainerSourceLabel?: string;
    onOpenDisposition: (journey: EventContainerWithEvents) => void;
    onCloseDisposition: () => void;
    isAmaron?: boolean;
}) {
    const {workspace, workspaceCapabilities} = useWorkspace();
    const eventContainerTemplates = useWorkspaceEventContainerTemplates();
    const footerBindings = useOttopilotListCardFooterBindings();
    const archiveEngagement = useEventContainerArchiveEngagement({queryKeyPrefix, controllerRef});
    const scheduleCall = useScheduleCallEngagement({
        navigate,
        workspaceId,
        scheduleRoute,
        onSuccess: onScheduleSuccess,
        scheduleCallDisableConfig,
    });
    const logCallRoute = useMemo(
        () => resolveWorkspaceLogCallRoute(workspace, workspaceCapabilities),
        [workspace, workspaceCapabilities],
    );
    const hasLogCallAction = logCallRoute.trim() !== "";

    const capabilities = useMemo(
        () =>
            resolveEventContainerRowEngagementCapabilities({
                listConfig: listModuleConfig,
                showDisposition,
                showNotes,
                includeCreateDrawer,
                listCreateDrawerItemsCount,
                hasScheduleAction: scheduleCall.hasScheduleAction,
                hasLogCallAction,
                isArchiveAvailable: archiveEngagement.isArchiveAvailable,
                isArchivedView,
                isInQueueView,
            }),
        [
            listModuleConfig,
            showDisposition,
            showNotes,
            includeCreateDrawer,
            listCreateDrawerItemsCount,
            scheduleCall.hasScheduleAction,
            hasLogCallAction,
            archiveEngagement.isArchiveAvailable,
            isArchivedView,
            isInQueueView,
        ],
    );

    const handlersRef = useRef({
        footerBindings,
        selectedDispositionEventContainerId,
        onOpenDisposition,
        onCloseDisposition,
        openNotesPanel,
        closeNotesPanel,
        notesEventContainerSourceLabel,
        openScheduleCall: scheduleCall.openScheduleCall,
        archiveEngagement,
        logCallRoute,
        navigate,
        workspaceId,
    });
    handlersRef.current = {
        footerBindings,
        selectedDispositionEventContainerId,
        onOpenDisposition,
        onCloseDisposition,
        openNotesPanel,
        closeNotesPanel,
        notesEventContainerSourceLabel,
        openScheduleCall: scheduleCall.openScheduleCall,
        archiveEngagement,
        logCallRoute,
        navigate,
        workspaceId,
    };

    const dismissAsidePanels = (options?: {keepNotesPanel?: boolean}) => {
        const b = handlersRef.current.footerBindings;
        dismissDataViewerAsidePanels({
            controllerRef,
            closeCreateDrawer: () => {
                b.setSelectedCreateJourneyId(null);
                b.setSelectedDispositoinId(null);
            },
            closeNotesPanel:
                options?.keepNotesPanel === true ? undefined : handlersRef.current.closeNotesPanel,
            closeDisposition: handlersRef.current.onCloseDisposition,
            closeScheduleCall: scheduleCall.closeScheduleCallModal,
            dismissArchive: handlersRef.current.archiveEngagement.dismissArchive,
        });
    };

    const handlers: RowEngagementHandlers<EventContainerWithEvents> = useMemo(
        () => ({
            openDisposition: (journey) => {
                if (
                    handlersRef.current
                        .selectedDispositionEventContainerId === journey.id
                ) {
                    return;
                }
                dismissAsidePanels();
                handlersRef.current.onOpenDisposition(journey);
            },
            openCreate: (journey) => {
                dismissAsidePanels();
                const b = handlersRef.current.footerBindings;
                b.setSelectedCreateJourneyId(journey.id);
                b.setSelectedDispositoinId(
                    getEventContainerDispositionId(
                        journey,
                        requireEventContainerTemplate(eventContainerTemplates, journey.templateId),
                    ),
                );
            },
            openLogCall: (journey) => {
                dismissAsidePanels();
                const route = handlersRef.current.logCallRoute.trim();
                if (route === "") {
                    return;
                }
                navigateToScheduleCallRoute(
                    handlersRef.current.navigate,
                    route,
                    handlersRef.current.workspaceId,
                    journey.id,
                );
            },
            openNotes: (journey) => {
                dismissAsidePanels({keepNotesPanel: true});
                handlersRef.current.openNotesPanel({
                    eventContainerId: journey.id,
                    eventContainer: journey,
                    createScope: {scopeType: "eventContainer"},
                    eventContainerSourceLabel: handlersRef.current.notesEventContainerSourceLabel ?? "Journey",
                });
            },
            openSchedule: (journey) => {
                dismissAsidePanels();
                handlersRef.current.openScheduleCall(journey);
            },
        }),
        [eventContainerTemplates],
    );

    const profileNavigationFrom = useMemo(
        () =>
            buildEventContainerProfileFromForList({
                isArchivedView,
                isInQueueView,
            }),
        [isArchivedView, isInQueueView],
    );

    const tableRowActions: TableRowActionsConfig<EventContainerWithEvents> = useMemo(
        () => ({
            toolbar: capabilities.toolbar,
            moreMenu: capabilities.moreMenu,
            onProfile: (journey) => {
                const controller = controllerRef.current;
                if (controller === null) {
                    return;
                }
                dismissAsidePanels();
                controller.setCurrentlyOpenRowEngagementPanel({
                    rowEngagementType: RowEngagementType.read,
                    rowEngagementPanelType: RowEngagementPanelType.sidebar,
                    rowEngagementData: journey,
                    readPanelLayout: "entityProfile",
                    hidePanelLayoutToggle: true,
                });
            },
            onDisposition: handlers.openDisposition,
            onEdit: (journey) => {
                const controller = controllerRef.current;
                if (controller === null || controller.canEditRow?.(journey) === false) {
                    return;
                }
                dismissAsidePanels();
                controller.setCurrentlyOpenRowEngagementPanel({
                    rowEngagementType: RowEngagementType.update,
                    rowEngagementPanelType:
                        controller.updatePanelOpensInFullscreen === true
                            ? RowEngagementPanelType.fullscreen
                            : RowEngagementPanelType.sidebar,
                    rowEngagementData: journey,
                });
            },
            onView: (journey) => {
                const controller = controllerRef.current;
                if (controller === null) {
                    return;
                }
                dismissAsidePanels();
                controller.setCurrentlyOpenRowEngagementPanel({
                    rowEngagementType: RowEngagementType.read,
                    rowEngagementPanelType: RowEngagementPanelType.sidebar,
                    rowEngagementData: journey,
                });
            },
            onCreate: handlers.openCreate,
            onLogCall: handlers.openLogCall,
            onNotes: handlers.openNotes,
            onScheduleCall: handlers.openSchedule,
            onArchive: (journey) => {
                dismissAsidePanels();
                archiveEngagement.requestArchive(journey);
            },
            canEdit: (row) => {
                if (isEventContainerRowActionDisabledByGlobalConstraints("edit", row, globalConstraints) === true) {
                    return false;
                }
                if (isInQueueView === true && canEditInQueueJourney !== undefined) {
                    return canEditInQueueJourney(row);
                }
                return controllerRef.current?.canEditRow?.(row) ?? true;
            },
            isToolbarItemDisabled: (item, journey) =>
                isEventContainerRowActionDisabledByGlobalConstraints(item, journey, globalConstraints),
            isMenuItemDisabled: (item, journey) => {
                if (isEventContainerRowActionDisabledByGlobalConstraints(item, journey, globalConstraints) === true) {
                    return true;
                }
                if (item === "archive") {
                    if (isEventContainerEngagementRestrictedByGlobalConstraints(journey, globalConstraints) === true) {
                        return false;
                    }
                    return archiveEngagement.getArchiveDisabledReason(journey) !== null;
                }
                if (item === "scheduleCall") {
                    return getScheduleCallDisabledReason(journey, scheduleCallDisableConfig) !== null;
                }
                return false;
            },
            getActiveState: (journey) => {
                const controller = controllerRef.current;
                if (controller === null) {
                    return {};
                }
                const panel = controller.currentlyOpenRowEngagementPanel;
                const bindings = footerBindings;
                const readOpen =
                    panel.rowEngagementType === RowEngagementType.read &&
                    panel.rowEngagementData?.id === journey.id;
                const isEntityProfileAside =
                    readOpen === true && panel.readPanelLayout === "entityProfile";
                const view = readOpen === true && isEntityProfileAside !== true;
                const edit =
                    panel.rowEngagementType === RowEngagementType.update &&
                    panel.rowEngagementData?.id === journey.id;
                const create =
                    bindings.selectedCreateJourneyId === journey.id &&
                    bindings.selectedDispositionId !== null;
                const notes = notesPanelEventContainerId === journey.id;
                const scheduleCallActive =
                    scheduleCall.activeJourneyId != null && scheduleCall.activeJourneyId === journey.id;
                const archive =
                    archiveEngagement.archiveTargetEventContainerId === journey.id;
                const more =
                    capabilities.toolbar.includes("more") === true &&
                    (edit || create || notes || scheduleCallActive || archive);

                return {
                    disposition:
                        selectedDispositionEventContainerId === journey.id,
                    edit,
                    view,
                    profile: isEntityProfileAside,
                    create,
                    notes,
                    scheduleCall: scheduleCallActive,
                    archive,
                    more,
                };
            },
            menuItemLabels: isAmaron === true ? {profile: "Profile"} : undefined,
        }),
        [
            capabilities,
            controllerRef,
            footerBindings,
            handlers,
            archiveEngagement,
            selectedDispositionEventContainerId,
            notesPanelEventContainerId,
            scheduleCall.activeJourneyId,
            isInQueueView,
            canEditInQueueJourney,
            globalConstraints,
            scheduleCallDisableConfig,
            isAmaron,
        ],
    );

    return {
        handlers,
        footerBindings,
        tableRowActions,
        archiveModals: archiveEngagement.archiveModals,
        scheduleCallModal: scheduleCall.scheduleCallModal,
        scheduleCall,
        profileNavigationFrom,
    };
}

export type EventContainerRowEngagementResult = ReturnType<
    typeof useEventContainerRowEngagement
>;
